import { useState, useEffect, useLayoutEffect } from "react";
// import { gpx } from "@mapbox/togeojson";
import { gpx } from "@tmcw/togeojson";
import { zipWith } from "lodash";
import haversine from "haversine";
import { differenceInSeconds } from "date-fns";

// Naive custom hook for fetching stuff
export function useFetcher(fetchSomethingAPI, inputs) {
  const [data, setData] = useState();
  const [isFetching, setIsFetching] = useState();

  useLayoutEffect(() => {
    setIsFetching(true);

    fetchSomethingAPI()
      .then(async (response) => {
        setData(await response);
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, inputs);

  return [data, isFetching];
}

// GPX parser
export function convertGpxToJson(routeGpx) {
  if (!routeGpx) {
    return;
  }

  // Convert GPX XML to GeoJSON
  // let gpxParser = require('gpxparser');
  // var gpx = new gpxParser();

  const parsed = new DOMParser().parseFromString(routeGpx, "application/xml");
  const converted = gpx(parsed);
  // gpx.parse(routeGpx);
  // const converted = gpx.toGeoJSON(); //parse gpx file from string data

  if(!converted.features || !converted.features.length) {
    console.warn('Wrong GPX format');
    return null;
  }

  // Combine two arrays into one
  let route = zipWith(
    converted.features[0].geometry.coordinates,
    converted.features[0].properties.coordTimes,
    function (a, b) {
      return {
        lon: a[0],
        lat: a[1],
        elevation: a[2],
        time: b,
        distance: 0,
      };
    }
  );

  // Calculate additional information about route and each route point, for example distance and slope

  const totalTime = differenceInSeconds(
    new Date(route[route.length - 1].time),
    new Date(route[0].time)
  );

  let totalPositiveElevation = 0;
  let totalNegativeElevation = 0;
  let totalDistance = 0;
  route[0].cumulatedAscent = 0;
  route[0].cumulatedDescent = 0;

  for (let i = 0; i < route.length - 1; i++) {
    const start = {
      latitude: route[i].lat,
      longitude: route[i].lon,
    };

    const end = {
      latitude: route[i + 1].lat,
      longitude: route[i + 1].lon,
    };

    // Haversine formula determines distance between two points based on their coordinates
    totalDistance += haversine(start, end);
    route[i + 1].distance = totalDistance;
  
    // Fix in case gpx misses elevation data on some points
    if (typeof route[i + 1].elevation === 'undefined') {
      route[i + 1].elevation = route[i].elevation
    }

    const distanceChange = route[i + 1].distance - route[i].distance;
    const elevationChange = route[i + 1].elevation - route[i].elevation;

    if (elevationChange > 0) {
      totalPositiveElevation += elevationChange;
    } else {
      totalNegativeElevation += elevationChange;
    }

    route[i + 1].cumulatedAscent = totalPositiveElevation;
    route[i + 1].cumulatedDescent = totalNegativeElevation;

    route[i + 1].slope = (elevationChange / (distanceChange * 1000)) * 100;
  }

  return {
    route,
    totalPositiveElevation,
    totalNegativeElevation,
    totalTime,
  };
}

// Super naive slope color generator
// Color fixed to match map trace
export function getSlopeColor(slope) {
  /* let green = 180;
  let red = slope * 255 / 8;

  if (slope > 8) {
    green = green - ((slope - 3) * 100 / 12);
  } */

  let green = 69;
  let red = 255;

  return `rgba(${red}, ${green}, 0)`;
}

export function secondsToHm(d) {
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);

  return `${h}:${m.toLocaleString(undefined, { minimumIntegerDigits: 2 })}`;
}

export function secondsToHms(d) {
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor(d - h * 3600 - m * 60);

  return `${m.toLocaleString(undefined, {
    minimumIntegerDigits: 2,
  })}:${s.toLocaleString(undefined, { minimumIntegerDigits: 2 })}`;
}

// Hook to handle window resize
export function useWindowSize(ref) {
  // Initialize state with undefined width/height so server and client renders match
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: ref.current.clientWidth,
        height: ref.current.clientHeight,
      });
    }
    // Add event listener
    window.addEventListener("resize", handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
}

const calculateAverageElevation = (state) => {
  let weightedElevation = [];
  let distance = [];
  if (state.selectedRoute) {
    for (var i = 1; i < state.selectedRoute.route.length; i++) {
      weightedElevation[i] = state.selectedRoute.route[i].elevation * state.selectedRoute.route[i].distance;
      distance[i] = state.selectedRoute.route[i].distance;
      }
    let cumElevation = weightedElevation.reduce(function(a, b){
      return a + b;
    }, 0);
    let thisDistance = distance.reduce(function(a, b){
      return a + b;
    }, 0);
    // console.log(state.selectedRoute.route[0].lat +","+ state.selectedRoute.route[0].lon)
    return cumElevation / thisDistance;
  }
}

const calculateAverageElevationV2 = (selectedRoute) => {
  let weightedElevation = [];
  let distance = [];
  if (selectedRoute) {
    for (var i = 1; i < selectedRoute.route.length; i++) {
      weightedElevation[i] = selectedRoute.route[i].elevation * selectedRoute.route[i].distance;
      distance[i] = selectedRoute.route[i].distance;
      }
    let cumElevation = weightedElevation.reduce(function(a, b){
      return a + b;
    }, 0);
    let thisDistance = distance.reduce(function(a, b){
      return a + b;
    }, 0);
    // console.log(state.selectedRoute.route[0].lat +","+ state.selectedRoute.route[0].lon)
    return cumElevation / thisDistance;
  }
}

export {
  calculateAverageElevation,
  calculateAverageElevationV2,
};